import { useCallback, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SandboxRenderResult } from "../../../components/UIToolkit/Previews/SandboxedHtmlRenderer";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { AiPrintoutData, AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import { REFERENCE_TEMPLATE_SOURCE } from "../../../downloads/aiPrintout/referenceTemplateSource";
import { type CustomPrintoutGenerationOutcome, GENERATION_FAILED_KEY, useCustomPrintoutGeneration } from "../../../hooks/useCustomPrintoutGeneration";
import { type ChatEntry, type CustomPrintoutDraft, type DraftAction, draftReducer, INITIAL_DRAFT_STATE, type RepairRequest } from "./customPrintoutDraftState";
import { buildRepair, renderFailureOf } from "./customPrintoutRepair";
import { type CustomPrintoutGenerationRequest, submitRequestOf } from "./customPrintoutRequests";
import type { CustomPrintoutCoverage, CustomPrintoutDetail, CustomPrintoutEffort } from "./customPrintoutTypes";
import { useAutoRepair } from "./useAutoRepair";
import { useCompletenessFailureTracking } from "./useCompletenessFailureTracking";
import { useDraftComposer } from "./useDraftComposer";
import { ownerKeyOf, useDraftPersistence } from "./useDraftPersistence";

type Params = {
	/** The saved printout being shown, or `null` for a new one (which starts from the reference template). */
	printoutId: string | null;
	/** That printout's current version, once loaded. */
	saved: CustomPrintoutDetail | null;
	/**
	 * A new printout that starts from a saved one: that printout's id, and its current version once loaded. `null`
	 * starts from the reference sheet. Ignored while a saved printout is shown.
	 */
	startFrom: CustomPrintoutStartFrom | null;
	/** The viewed day, or `null` while it loads. Designing needs it: the design context is built from it. */
	data: AiPrintoutData | null;
};

/** The saved printout a new one starts from, and its current version once loaded (`null` while loading). */
export type CustomPrintoutStartFrom = { printoutId: string; detail: CustomPrintoutDetail | null };

/**
 * What a request was, for the chat line that records how it went — whose printout it was for, and at what thinking
 * level. `composerText` goes back into the composer if it fails. `retry` is the request itself, sent again as a
 * summary if Annie asks and the user agrees; `null` for one that can't be asked about (the automatic repair).
 */
type Asked = {
	request: string;
	hasAttachment: boolean;
	isRepair: boolean;
	owner: string | null;
	shifts: AiPrintoutShift[];
	effort: CustomPrintoutEffort;
	composerText: string;
	retry: CustomPrintoutGenerationRequest | null;
};

/** What the template on screen shows: the draft's, else the saved version's, else the copied printout's; the reference sheet lists everyone. */
const shownCoverageOf = (
	draft: CustomPrintoutDraft | null,
	saved: CustomPrintoutDetail | null,
	startFrom: CustomPrintoutStartFrom | null,
): CustomPrintoutCoverage => {
	if (draft !== null) return draft.coverage;
	if (saved !== null) return saved.coverage;
	return startFrom === null || startFrom.detail === null ? "every_shift" : startFrom.detail.coverage;
};

/**
 * The template on screen: the unsaved draft, else the saved version, else — for a new printout — the saved printout
 * it starts from, or the reference sheet.
 */
const previewSourceOf = (
	draft: CustomPrintoutDraft | null,
	saved: CustomPrintoutDetail | null,
	printoutId: string | null,
	startFrom: CustomPrintoutStartFrom | null,
): string | null => {
	if (draft !== null) return draft.source;
	if (saved !== null) return saved.source;
	// `null` = the saved printout's version (or the one a new printout copies) is still loading.
	if (printoutId !== null) return null;
	if (startFrom === null) return REFERENCE_TEMPLATE_SOURCE;
	return startFrom.detail === null ? null : startFrom.detail.source;
};

/** The action recording how a design request ended; `null` when nothing should change (cancelled). */
const actionFor = (outcome: CustomPrintoutGenerationOutcome, asked: Asked, shownSource: string | null): DraftAction | null => {
	if (outcome.status === "cancelled") return null;
	if (outcome.status === "failed") return { type: "generationFailed", request: asked.request, errorKey: outcome.errorKey };
	if (outcome.status === "needsConfirmation") {
		// The automatic repair fixes a template; a question about what kind of sheet it is has no one to answer it.
		if (asked.retry === null) return { type: "generationFailed", request: asked.request, errorKey: GENERATION_FAILED_KEY };
		return { type: "summaryQuestionAsked", request: asked.request, hasAttachment: asked.hasAttachment, summary: outcome.summary, retry: asked.retry };
	}
	return {
		type: "generationSucceeded",
		draft: { generationId: outcome.generationId, source: outcome.source, summary: outcome.summary, effort: asked.effort, coverage: outcome.coverage },
		turn: { request: asked.request, summary: outcome.summary, isRepair: asked.isRepair, hasAttachment: asked.hasAttachment },
		sameSourceAsShown: outcome.source === shownSource,
		shifts: asked.shifts,
	};
};

/**
 * The Custom printout's draft: what's on screen, what Save would keep, and the chat that got there. Owns the
 * state machine in `customPrintoutDraftState` and runs its side effects: the design jobs, the single
 * automatic repair after a render that leaves people out, and the analytics.
 *
 * Showing another `printoutId` (or starting a new one from another saved printout) starts over and stops any design
 * still running for the one left (the caller asks the user first while `isDirty`); an answer that still arrives for
 * the old one is dropped. The newest unsaved design is also kept in this browser, so a reload brings it back
 * (`useDraftPersistence`).
 */
export const useCustomPrintoutDraft = ({ printoutId, saved, startFrom, data }: Params) => {
	const { t } = useTranslation();
	const { run: runGeneration, cancel: cancelGeneration } = useCustomPrintoutGeneration();
	const [state, dispatch] = useReducer(draftReducer, INITIAL_DRAFT_STATE);
	const composer = useDraftComposer();
	const trackFailure = useCompletenessFailureTracking(printoutId);

	const startFromId = startFrom === null ? null : startFrom.printoutId;
	const ownerKey = ownerKeyOf(printoutId, startFromId);
	const [ownerId, setOwnerId] = useState<string | null>(ownerKey);
	if (ownerId !== ownerKey) {
		setOwnerId(ownerKey);
		cancelGeneration();
		dispatch({ type: "reset" });
		composer.clear();
	}
	const ownerRef = useRef<string | null>(ownerKey);
	ownerRef.current = ownerKey;
	const previewSource = previewSourceOf(state.draft, saved, printoutId, startFrom);
	const shownSourceRef = useRef<string | null>(previewSource);
	shownSourceRef.current = previewSource;
	const dataRef = useRef<AiPrintoutData | null>(data);
	dataRef.current = data;

	const { restore } = composer;
	const settle = useCallback(
		(outcome: CustomPrintoutGenerationOutcome, asked: Asked): void => {
			const action = asked.owner === ownerRef.current ? actionFor(outcome, asked, shownSourceRef.current) : null;
			if (action === null) return;
			dispatch(action);
			if (action.type === "generationFailed") restore(asked.composerText);
		},
		[restore],
	);

	const submit = useSubmit({ printoutId, ownerKey, saved, startFrom, data, draft: state.draft, composer, runGeneration, settle, dispatch });

	const repairShifts = data === null ? null : data.shifts;
	const settleRepair = useCallback(
		(outcome: CustomPrintoutGenerationOutcome, repairRequest: RepairRequest) => {
			if (repairShifts !== null)
				settle(outcome, {
					request: "",
					hasAttachment: false,
					isRepair: true,
					owner: ownerKey,
					shifts: repairShifts,
					effort: repairRequest.effort,
					composerText: "",
					retry: null,
				});
		},
		[settle, ownerKey, repairShifts],
	);
	useAutoRepair({ repairRequest: state.repairRequest, printoutId, data, runGeneration, onSettled: settleRepair });

	const onReport = useCallback(
		(result: SandboxRenderResult) => {
			const day = dataRef.current;
			if (day === null) throw new Error("the custom printout preview reported a render with no day data");
			dispatch({ type: "reported", result, shifts: day.shifts });
			trackFailure(result, shownSourceRef.current);
		},
		[trackFailure],
	);

	const failure = useMemo(() => renderFailureOf(state.report), [state.report]);
	const { fill } = composer;
	const fixWithAnnie = useCallback(() => {
		if (failure === null || data === null) return;
		fill(t(failure.kind === "incomplete" ? "printout.custom.ai.fix.prompt.missing" : "printout.custom.ai.fix.prompt.error"), buildRepair(failure, data.shifts));
	}, [failure, data, t, fill]);

	const forgetStored = useDraftPersistence({
		printoutId,
		startFromId,
		ownerKey,
		saved,
		draft: state.draft,
		entries: state.entries,
		shifts: repairShifts,
		ownerRef,
		shownSourceRef,
		isPristine: state.draft === null && state.phase === "idle" && state.entries.length === 0,
		dispatch,
	});

	const { clear } = composer;
	// Saved or thrown away: either way there is no unsaved design left to bring back after a reload.
	const discard = useCallback(() => {
		cancelGeneration();
		dispatch({ type: "reset" });
		clear();
		forgetStored();
	}, [cancelGeneration, clear, forgetStored]);

	const answerSummaryQuestion = useAnswerSummaryQuestion({ openQuestion: state.openQuestion, ownerKey, data, runGeneration, settle, dispatch });
	const coverage = shownCoverageOf(state.draft, saved, printoutId === null ? startFrom : null);
	return useDraftHandle({ state, saved, previewSource, coverage, failure, composer, onReport, submit, discard, fixWithAnnie, answerSummaryQuestion });
};

type AnswerParams = {
	openQuestion: CustomPrintoutGenerationRequest | null;
	ownerKey: string | null;
	data: AiPrintoutData | null;
	runGeneration: ReturnType<typeof useCustomPrintoutGeneration>["run"];
	settle: (outcome: CustomPrintoutGenerationOutcome, asked: Asked) => void;
	dispatch: (action: DraftAction) => void;
};

/**
 * The user's answer to Annie's "make it a summary?": yes sends the same request again — words, example and all — as a
 * confirmed summary; no leaves the printout as it is. The chat records the yes as its own request.
 */
const useAnswerSummaryQuestion = ({ openQuestion, ownerKey, data, runGeneration, settle, dispatch }: AnswerParams) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	return useCallback(
		async (accepted: boolean): Promise<void> => {
			if (openQuestion === null || data === null) return;
			dispatch({ type: "summaryQuestionAnswered", accepted });
			track("custom_printout_summary_question_answered", { department_id: chosenDepartment.department_id, accepted });
			if (!accepted) return;
			const request = t("printout.custom.ai.summary.question.yes.request");
			dispatch({ type: "submitted", pending: { request, hasAttachment: false } });
			const asked: Asked = {
				request,
				hasAttachment: false,
				isRepair: false,
				owner: ownerKey,
				shifts: data.shifts,
				effort: openQuestion.effort,
				composerText: "",
				retry: null,
			};
			settle(await runGeneration({ ...openQuestion, allowsSummary: true }), asked);
		},
		[openQuestion, data, dispatch, track, chosenDepartment.department_id, t, ownerKey, settle, runGeneration],
	);
};

type SubmitParams = {
	printoutId: string | null;
	ownerKey: string | null;
	saved: CustomPrintoutDetail | null;
	startFrom: CustomPrintoutStartFrom | null;
	data: AiPrintoutData | null;
	draft: CustomPrintoutDraft | null;
	composer: ReturnType<typeof useDraftComposer>;
	runGeneration: ReturnType<typeof useCustomPrintoutGeneration>["run"];
	settle: (outcome: CustomPrintoutGenerationOutcome, asked: Asked) => void;
	dispatch: (action: DraftAction) => void;
};

/** Sends what's in the composer, refining the draft if there is one, else the saved version. */
const useSubmit = ({ printoutId, ownerKey, saved, startFrom, data, draft, composer, runGeneration, settle, dispatch }: SubmitParams) => {
	const { prompt, file, fixRepair, effort, clear } = composer;
	const startFromDetail = startFrom === null ? null : startFrom.detail;
	// A copy whose template hasn't loaded would go out with no base and design from scratch; the composer waits for it.
	const isStartLoading = printoutId === null && startFrom !== null && startFromDetail === null;
	return useCallback(async (): Promise<void> => {
		if (data === null || isStartLoading) return;
		const request = submitRequestOf({
			printoutId,
			prompt,
			file,
			fixRepair,
			effort,
			draft,
			saved,
			startFrom: printoutId === null ? startFromDetail : null,
			data,
		});
		// The chat records the words as typed (trimmed); a request of only an example has none.
		const words = request.prompt === null ? "" : request.prompt;
		const asked: Asked = {
			request: words,
			hasAttachment: file !== null,
			isRepair: request.repair !== null,
			owner: ownerKey,
			shifts: data.shifts,
			effort,
			composerText: words,
			retry: request,
		};
		dispatch({ type: "submitted", pending: { request: words, hasAttachment: asked.hasAttachment } });
		clear();
		settle(await runGeneration(request), asked);
	}, [data, isStartLoading, prompt, draft, saved, startFromDetail, fixRepair, effort, file, clear, runGeneration, printoutId, ownerKey, settle, dispatch]);
};

type HandleParams = {
	state: ReturnType<typeof draftReducer>;
	saved: CustomPrintoutDetail | null;
	previewSource: string | null;
	coverage: CustomPrintoutCoverage;
	answerSummaryQuestion: (accepted: boolean) => Promise<void>;
	failure: ReturnType<typeof renderFailureOf>;
	composer: ReturnType<typeof useDraftComposer>;
	onReport: (result: SandboxRenderResult) => void;
	submit: () => Promise<void>;
	discard: () => void;
	fixWithAnnie: () => void;
};

/** What the Custom mode reads from and does to the draft. */
const useDraftHandle = ({
	state,
	saved,
	previewSource,
	coverage,
	failure,
	composer,
	onReport,
	submit,
	discard,
	fixWithAnnie,
	answerSummaryQuestion,
}: HandleParams) => {
	const entries = useMemo<ChatEntry[]>(
		() => [
			...(saved === null ? [] : saved.conversation.map(({ author, ...turn }): ChatEntry => ({ kind: "turn", turn, author: { kind: "saved", author } }))),
			...state.entries,
		],
		[saved, state.entries],
	);
	const isGenerating = state.phase === "generating" || state.phase === "repairing";
	return {
		phase: state.phase,
		draft: state.draft,
		entries,
		pending: state.pending,
		report: state.report,
		previewSource,
		/** What the template on screen shows: a summary is checked only for drawing, not for everyone being on it. */
		coverage,
		answerSummaryQuestion,
		onReport,
		submit,
		discard,
		fixWithAnnie,
		canFix: failure !== null && !isGenerating,
		prompt: composer.prompt,
		setPrompt: composer.setPrompt,
		file: composer.file,
		setFile: composer.setFile,
		effort: composer.effort,
		setEffort: composer.setEffort,
		composerFocusKey: composer.focusKey,
		isGenerating,
		/** Something on screen would be lost by switching printouts or closing. */
		isDirty: state.draft !== null || isGenerating,
		/** Only a finished draft whose latest render shows everyone. */
		canSave: state.phase === "ready" && state.draft !== null && state.report.status === "rendered" && state.report.report.ok,
	};
};

/** Everything the Custom mode reads from and does to the draft. */
export type CustomPrintoutDraftHandle = ReturnType<typeof useCustomPrintoutDraft>;
